import { useState } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { SubmissionLayout, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import { ClipboardCheck, CheckCircle2, Circle, RefreshCw, Loader2, Building2, FileText, Clock, ArrowLeft } from "lucide-react";

export default function PostCompletionDetail({ submission, formatDate, navigate, isDirector, onDelete }) {
  const [syncing, setSyncing] = useState(false);
  const data = submission?.data || {};
  const header = data.header || {};
  const tasks = data.tasks || [];
  const completedCount = tasks.filter((t) => t.completed).length;
  const totalCount = tasks.length;
  const allDone = totalCount > 0 && completedCount === totalCount;
  const todoSyncedCount = tasks.filter((t) => t.todo_task_id).length;
  const pct = totalCount ? Math.round((completedCount / totalCount) * 100) : 0;

  const handleSyncTodo = async () => {
    setSyncing(true);
    try {
      const headers = {};
      const bonsaiUserId = sessionStorage.getItem("bonsai_user_id");
      if (bonsaiUserId) headers["X-Bonsai-User-Id"] = bonsaiUserId;
      const res = await axios.post(`${API}/postcompletion/${submission.id}/sync-todo`, {}, { headers });
      const sync = res.data?.sync || {};
      if (sync.synced > 0) toast.success(`${sync.synced} task(s) synced to To Do`);
      else if (sync.errors?.length) toast.error(sync.errors[0]);
      else toast.info("No tasks flagged for To Do sync");
      if (sync.synced > 0) window.location.reload();
    } catch { toast.error("To Do sync failed"); }
    finally { setSyncing(false); }
  };

  const actions = (
    <>
      <Button variant="outline" size="sm" onClick={handleSyncTodo} disabled={syncing}
        className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="pc-sync-todo-btn">
        {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        Sync To Do
      </Button>
      <Button variant="outline" size="sm" onClick={() => navigate(`/post-completion?id=${submission.id}`)}
        className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="pc-edit-btn">
        Edit
      </Button>
    </>
  );

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={header.matter_reference || "Post-Completion Checklist"}
      subtitle={`${header.client_name || ""} \u2014 ${header.counterparty_name || ""}`}
      matterReference={header.matter_reference}
      badge="Post-Completion"
      badgeColor="indigo"
      badgeIcon={ClipboardCheck}
      isTest={false}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={actions}
    >
      {/* Status Row */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="pc-header-info">
        <div className="flex items-center gap-4">
          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${allDone ? "bg-teal-dim text-teal border-teal" : "bg-status-amber-dim text-status-amber border-amber-200"}`}>
            {allDone ? "Completed" : `${completedCount}/${totalCount} done`}
          </span>
          {todoSyncedCount > 0 && (
            <span className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="pc-todo-synced-badge">
              {todoSyncedCount} in To Do
            </span>
          )}
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="pc-bento-grid">

        {/* Matter Details — 8 cols */}
        <div className="md:col-span-8 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-matter-details">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Building2 className="w-4 h-4" /> Matter Details
            </h2>
          </div>
          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            {[
              { label: "Matter Ref", value: header.matter_reference },
              { label: "Client", value: header.client_name },
              { label: "Counterparty", value: header.counterparty_name },
              { label: "Transaction", value: header.transaction_type },
              { label: "Completion Date", value: header.completion_date },
              { label: "Fee Earner", value: header.fee_earner },
            ].map(({ label, value }) => (
              <div key={label}>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block mb-0.5">{label}</span>
                <span className="font-medium text-txt-faint">{value || "\u2014"}</span>
              </div>
            ))}
            {header.matter_description && (
              <div className="col-span-2 sm:col-span-3">
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block mb-0.5">Description</span>
                <span className="font-medium text-txt-faint">{header.matter_description}</span>
              </div>
            )}
          </div>
        </div>

        {/* Progress — 4 cols */}
        <div className="md:col-span-4 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-progress">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <ClipboardCheck className="w-4 h-4" /> Progress
            </h2>
          </div>
          <div className="p-5 text-center space-y-4">
            <div className="relative w-24 h-24 mx-auto">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#E0DDD6" strokeWidth="8" />
                <circle cx="50" cy="50" r="42" fill="none" stroke={allDone ? "#4f46e5" : "#F59E0B"} strokeWidth="8"
                  strokeDasharray={`${pct * 2.64} 264`} strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-txt-faint">{pct}%</span>
              </div>
            </div>
            <p className="text-sm text-txt-faint">{completedCount} of {totalCount} tasks completed</p>
          </div>
        </div>

        {/* Tasks Table — 12 cols */}
        <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-tasks">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <FileText className="w-4 h-4" /> Tasks
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[color:var(--surface-inset)] text-left text-xs font-medium text-txt-faint uppercase tracking-wider">
                  <th className="px-4 py-3 w-8"></th>
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Description</th>
                  <th className="px-4 py-3">Due Date</th>
                  <th className="px-4 py-3">Responsible</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3 w-16 text-center">To Do</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-border/60">
                {tasks.map((task, i) => (
                  <tr key={task.id || i} className={task.completed ? "bg-teal-dim/40" : ""}>
                    <td className="px-4 py-3 text-center">
                      {task.completed ? <CheckCircle2 className="w-4 h-4 text-teal" /> : <Circle className="w-4 h-4 text-txt" />}
                    </td>
                    <td className="px-4 py-3 text-xs text-txt-muted">{task.category || "\u2014"}</td>
                    <td className="px-4 py-3 font-medium text-txt-faint">{task.description}</td>
                    <td className="px-4 py-3 text-xs text-txt-faint">{task.due_date || "\u2014"}</td>
                    <td className="px-4 py-3 text-xs text-txt-faint">{task.responsible || "\u2014"}</td>
                    <td className="px-4 py-3 text-xs text-txt-muted">{task.notes || "\u2014"}</td>
                    <td className="px-4 py-3 text-center">
                      {task.todo_task_id ? (
                        <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-testid={`pc-detail-synced-${i}`}>Synced</span>
                      ) : task.sync_to_todo ? (
                        <span className="text-[10px] text-status-amber" data-testid={`pc-detail-pending-${i}`}>Pending</span>
                      ) : (
                        <span className="text-[10px] text-txt">{"\u2014"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </SubmissionLayout>
    {header.matter_reference && (
      <BonsaiMate context="matter" matterReference={header.matter_reference} />
    )}
    </>
  );
}
